<template>
  <div class="cell is-col-span-1 is-flex"
    style="position: relative"
    :inert="isSaving"
    @focusout="onComponentFocusOut">
    <div v-if="isSaving"
      class="quote-item-saving-overlay" />
    <article class="box is-flex-grow-1 main-container">
      <div class="columns mb-0">
        <div class="column is-flex is-align-items-center">
          <span class="mso mso-24px mr-2">calendar_clock</span>
          <p class="title pl-1">Key Dates</p>
        </div>
      </div>
      <div class="is-divider" />
      <div class="field">
        <label class="label">Request for Quote Date</label>
        <div class="control">
          <input v-if="readOnlyView"
            class="input is-static"
            :value="$filters.formatDateLocale(innerQuote.quoteDate, $userInfo.locale)"
            readonly>
          <v-date-picker v-else
            :is-dark="$theme.isDark"
            v-model="innerQuoteDate"
            :masks="formats"
            :attributes="attrs"
            :locale="$userInfo.locale"
            :timezone="$filters.getIanaTimezone()">
            <template v-slot="{ inputValue, inputEvents }">
              <div class="field">
                <div class="control has-icons-left">
                  <input type="text"
                    class="input"
                    placeholder="Quote Date"
                    :value="inputValue"
                    v-on="inputEvents">
                  <span class="icon is-small is-left">
                    <i class="mdi mdi-calendar mdi-18px" />
                  </span>
                </div>
              </div>
            </template>
          </v-date-picker>
        </div>
      </div>

      <div class="field is-horizontal">
        <div class="field-body columns">
          <div class="field column mr-0">
            <div class="field">
              <label class="label">Inspection Booking</label>
              <input v-if="readOnlyView"
                class="input is-static"
                :value="$filters.formatDateLocale(innerValue.inspectionBookingDate, $userInfo.locale)"
                readonly>
              <v-date-picker v-else
                :is-dark="$theme.isDark"
                v-model="innerInspectionBookingDate"
                ref="datepicker"
                mode="dateTime"
                :minute-increment="5"
                :masks="formats"
                :attributes="attrs"
                :locale="$userInfo.locale"
                :model-config="innerInspectionBookingDate ? {} : modelConfig"
                :timezone="$filters.getIanaTimezone()">
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="field is-fullwidth">
                    <div class="control has-icons-left">
                      <input type="text"
                        class="input"
                        placeholder="Inspection Start Date"
                        :value="inputValue"
                        v-on="inputEvents"
                        :disabled="isRepx || innerQuote.isArriveForInspection">
                      <span class="icon is-small is-left">
                        <i class="mdi mdi-calendar mdi-18px" />
                      </span>
                    </div>
                  </div>
                </template>
              </v-date-picker>
            </div>
          </div>
          <div class="field column mr-0">
            <label class="label">Booking Attended</label>
            <div class="control">
              <label>{{ innerQuote.isArriveForInspection ? 'Yes' : 'No' }}</label>
            </div>
          </div>
        </div>
      </div>

      <div class="field is-horizontal">
        <div class="field-body columns">
          <div class="field column mr-0">
            <label class="label">Sent for Authority</label>
            <div class="control">
              <input v-if="readOnlyView"
                class="input is-static"
                :value="$filters.formatDateLocale(innerValue.sentForAuthorizationDate, $userInfo.locale)"
                readonly>
              <v-date-picker v-else
                :is-dark="$theme.isDark"
                v-model="innerSentForAuthorizationDate"
                :masks="formats"
                :attributes="attrs"
                :locale="$userInfo.locale"
                :model-config="innerSentForAuthorizationDate ? {} : modelConfig"
                :timezone="$filters.getIanaTimezone()">
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="field">
                    <div class="control has-icons-left">
                      <input type="text"
                        class="input"
                        placeholder="Sent for Assessment"
                        :value="inputValue"
                        v-on="inputEvents">
                      <span class="icon is-small is-left">
                        <i class="mdi mdi-calendar mdi-18px" />
                      </span>
                    </div>
                  </div>
                </template>
              </v-date-picker>
            </div>
          </div>
          <div class="field column mr-0">
            <label class="label">Authority Date</label>
            <div class="control">
              <input v-if="readOnlyView"
                class="input is-static"
                :value="$filters.formatDateLocale(innerQuote.authorisedDate, $userInfo.locale)"
                readonly>
              <v-date-picker v-model="innerAuthorityDate"
                :is-dark="$theme.isDark"
                :masks="formats"
                :attributes="attrs"
                :locale="$userInfo.locale"
                :timezone="$filters.getIanaTimezone()">
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="field">
                    <div class="control has-icons-left">
                      <input type="text"
                        class="input"
                        placeholder="Authority Date"
                        :value="inputValue"
                        v-on="inputEvents">
                      <span class="icon is-small is-left">
                        <i class="mdi mdi-calendar mdi-18px" />
                      </span>
                    </div>
                  </div>
                </template>
              </v-date-picker>
            </div>
          </div>
        </div>
      </div>

      <div class="field is-horizontal">
        <div class="field-body columns">
          <div class="field column mr-0">
            <div v-if="innerQuote.isRepairBooked == null || (innerQuote.isRepairBooked != null && innerQuote.isRepairBooked)"
              class="field">
              <label class="label"
                :class="{ 'is-disabled': innerQuote.isRepairBooked == null }">
                Repair Booked Date
              </label>
              <input v-if="readOnlyView"
                class="input is-static"
                :value="$filters.formatDateLocale(innerValue.repairBookedDate, $userInfo.locale)"
                readonly>
              <v-date-picker v-else
                :is-dark="$theme.isDark"
                v-model="innerRepairBookedDate"
                ref="datepicker"
                mode="dateTime"
                :minute-increment="5"
                :masks="formats"
                :attributes="attrs"
                :locale="$userInfo.locale"
                class="is-fullwidth"
                :model-config="innerRepairBookedDate ? {} : modelConfig"
                :timezone="$filters.getIanaTimezone()">
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="field has-addons">
                    <div class="control is-expanded has-icons-left">
                      <input type="text"
                        class="input"
                        placeholder="Repair Booked Date"
                        :value="inputValue"
                        v-on="inputEvents"
                        :disabled="innerQuote.isRepairBooked == null">
                      <span class="icon is-small is-left">
                        <i class="mdi mdi-calendar mdi-18px" />
                      </span>
                    </div>
                    <div class="control">
                      <a class="button tooltip"
                        data-tooltip="Reset dates"
                        @click="clearRepairBookedDate()"
                        :disabled="innerQuote.isRepairBooked == null">
                        <span class="icon">
                          <i class="mdi mdi-close mdi-24px" />
                        </span>
                      </a>
                    </div>
                  </div>
                </template>
              </v-date-picker>
            </div>
            <div v-if="innerQuote.isRepairBooked != null && !innerQuote.isRepairBooked"
              class="field">
              <label class="label">Refuse Book Repair Reason</label>
              <div class="control is-expanded">
                <input class="input is-fullwidth"
                  :class="{ 'is-invalid': $v.entity.refuseBookRepairReason.$error }"
                  v-model="innerQuote.refuseBookRepairReason">
                <span class="help is-danger"
                  v-if="$v.entity && !$v.entity.refuseBookRepairReason.required">Refuse Book Repair Reason is required</span>
              </div>
            </div>
          </div>
          <div class="field column mr-0">
            <!-- <bulma-checkbox-date label="Is Repair Booked?"
                v-model="innerValue.isRepairBooked" /> -->
            <label class="label">Is Repair Booked</label>
            <div>
              <div class="pretty p-default p-round p-smooth ">
                <input id="chk-pretty-is-repair-booked-yes"
                  v-model="innerQuote.isRepairBooked"
                  name="chk-pretty-is-repair-booked"
                  :value="true"
                  type="radio">
                <div class="state p-primary">
                  <label>Yes</label>
                </div>
              </div>
              <div class="pretty p-default p-round p-smooth">
                <input id="chk-pretty-is-repair-booked-no"
                  v-model="innerQuote.isRepairBooked"
                  name="chk-pretty-is-repair-booked"
                  :value="false"
                  type="radio">
                <div class="state p-primary">
                  <label>No</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="field is-horizontal">
        <div class="field-body columns">
          <div class="field column mr-0">
            <div class="field">
              <label class="label">Estimated Start Date</label>
              <input v-if="readOnlyView || isRepairStartDateSynced"
                class="input is-static"
                :value="$filters.formatDateTimeLocale(innerQuote.jobStart, $userInfo.locale)"
                readonly>
              <v-date-picker v-else
                :is-dark="$theme.isDark"
                v-model="innerJobStartDate"
                mode="dateTime"
                :minute-increment="5"
                :masks="formats"
                :attributes="attrs"
                :locale="$userInfo.locale"
                :model-config="innerJobStartDate ? {} : modelConfig"
                :max-date="innerJobEndDate"
                :timezone="$filters.getIanaTimezone()">
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="field is-fullwidth">
                    <div class="control has-icons-left">
                      <input type="text"
                        class="input"
                        :class="{ 'is-invalid': $v.entity.jobStart.$error }"
                        placeholder="Job Start Date"
                        :value="inputValue"
                        v-on="inputEvents"
                        :content="ormEstimateStartEndValidationText"
                        v-tippy="{ theme: 'danger', enabled: $v.entity.jobStart.$error }">
                      <span class="icon is-small is-left">
                        <i class="mdi mdi-calendar mdi-18px" />
                      </span>
                    </div>
                  </div>
                </template>
              </v-date-picker>
              <span class="help is-info"
                v-if="isRepx">Once the Start Date is synced, it cannot be updated.</span>
            </div>
          </div>
          <div class="field column mr-0">
            <div class="field">
              <label class="label">Estimated End Date</label>
              <input v-if="readOnlyView"
                class="input is-static"
                :value="$filters.formatDateLocale(innerQuote.jobEnd, $userInfo.locale)"
                readonly>
              <v-date-picker v-else
                :is-dark="$theme.isDark"
                v-model="innerJobEndDate"
                mode="dateTime"
                :minute-increment="5"
                :masks="formats"
                :attributes="attrs"
                :locale="$userInfo.locale"
                class="is-fullwidth"
                :model-config="innerJobEndDate ? {} : modelConfig"
                :min-date="innerJobStartDate"
                :timezone="$filters.getIanaTimezone()">
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="field has-addons">
                    <div class="control is-expanded has-icons-left">
                      <input type="text"
                        class="input"
                        :class="{ 'is-invalid': $v.entity.jobEnd.$error }"
                        placeholder="Job End Date"
                        :value="inputValue"
                        v-on="inputEvents"
                        :content="ormEstimateStartEndValidationText"
                        v-tippy="{ theme: 'danger', enabled: $v.entity.jobEnd.$error }">
                      <span class="icon is-small is-left">
                        <i class="mdi mdi-calendar mdi-18px" />
                      </span>
                    </div>
                    <div class="control">
                      <a class="button tooltip"
                        data-tooltip="Reset dates"
                        @click="resetJobStartEnd()">
                        <span class="icon">
                          <i class="mdi mdi-close mdi-24px" />
                        </span>
                      </a>
                    </div>
                  </div>
                </template>
              </v-date-picker>
            </div>
          </div>
        </div>
      </div>

      <div class="field is-horizontal">
        <div class="field-body columns">
          <div class="field column mr-0">
            <label class="label">Vehicle Arrived</label>
            <input v-if="readOnlyView"
              class="input is-static"
              :value="$filters.formatDateLocale(innerValue.arrivedForRepairDate, $userInfo.locale)"
              readonly>
            <v-date-picker v-else
              :is-dark="$theme.isDark"
              v-model="innerArrivedForRepairDate"
              ref="datepicker"
              mode="dateTime"
              :minute-increment="5"
              :masks="formats"
              :attributes="attrs"
              :locale="$userInfo.locale"
              class="is-fullwidth"
              :model-config="innerArrivedForRepairDate ? {} : modelConfig"
              :timezone="$filters.getIanaTimezone()">
              <template v-slot="{ inputValue, inputEvents }">
                <div class="field has-addons">
                  <div class="control is-expanded has-icons-left">
                    <input type="text"
                      class="input"
                      placeholder="Arrive for Repair Date"
                      :value="inputValue"
                      v-on="inputEvents">
                    <span class="icon is-small is-left">
                      <i class="mdi mdi-calendar mdi-18px" />
                    </span>
                  </div>
                  <div class="control">
                    <a class="button tooltip"
                      data-tooltip="Reset dates"
                      @click="clearArrivedForRepairDate()">
                      <span class="icon">
                        <i class="mdi mdi-close mdi-24px" />
                      </span>
                    </a>
                  </div>
                </div>
              </template>
            </v-date-picker>
          </div>
          <div class="field column mr-0">
            <label class="label">Vehicle On Site</label>
            <div class="control">
              <div class="pretty p-switch p-fill">
                <input type="checkbox"
                  @change="isArrivedOnChange"
                  :checked="!!isArrived">
                <div class="state p-primary">
                  <label>{{ isArrived ? 'Yes' : 'No' }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- <div class="field">
        <label class="label">Vehicle Arrival Date</label>
        <div class="control">
          <v-date-picker v-model="vehicleArrivalDate"
            :masks="formats"
            :attributes="attrs"
            :locale="$userInfo.locale"
            @input="vehicleArrivalDateInputEvent"
            :timezone="$filters.getIanaTimezone()">
            <template v-slot="{ inputValue, inputEvents }">
              <div class="field">
                <div class="control has-icons-left">
                  <input type="text"
                    class="input"
                    placeholder="Vehicle Arrival Date"
                    :value="inputValue"
                    v-on="inputEvents"
                    readonly>
                  <span class="icon is-small is-left">
                    <i class="mdi mdi-calendar mdi-18px" />
                  </span>
                </div>
              </div>
            </template>
          </v-date-picker>
        </div>
      </div> -->

      <div class="field is-horizontal">
        <div class="field-body columns">
          <div class="field column mr-0">
            <label class="label">Job Start Date</label>
            <input v-if="readOnlyView"
              class="input is-static"
              :value="$filters.formatDateLocale(innerValue.actualJobStart, $userInfo.locale)"
              readonly>
            <v-date-picker v-else
              v-model="innerActualJobStart"
              :is-dark="$theme.isDark"
              mode="dateTime"
              :minute-increment="5"
              :masks="formats"
              :attributes="attrs"
              :locale="$userInfo.locale"
              :model-config="innerActualJobStart ? {} : modelConfig"
              :max-date="jobCompletionDateTime"
              :timezone="$filters.getIanaTimezone()">
              <template v-slot="{ inputValue, inputEvents }">
                <div class="field is-fullwidth">
                  <div class="control has-icons-left">
                    <input type="text"
                      class="input"
                      placeholder="Job Start"
                      :value="inputValue"
                      v-on="inputEvents"
                      readonly>
                    <span class="icon is-small is-left">
                      <i class="mdi mdi-calendar mdi-18px" />
                    </span>
                  </div>
                </div>
              </template>
            </v-date-picker>
          </div>
          <div class="field column mr-0">
            <label class="label">Job Completed Date</label>
            <input v-if="readOnlyView"
              class="input is-static"
              :value="$filters.formatDateLocale(innerValue.actualJobEnd, $userInfo.locale)"
              readonly>
            <v-date-picker v-else
              v-model="innerActualJobEnd"
              :is-dark="$theme.isDark"
              mode="dateTime"
              :minute-increment="5"
              :masks="formats"
              :attributes="attrs"
              :locale="$userInfo.locale"
              class="is-fullwidth"
              :model-config="innerActualJobEnd ? {} : modelConfig"
              :min-date="innerActualJobStart"
              :timezone="$filters.getIanaTimezone()">
              <template v-slot="{ inputValue, inputEvents }">
                <div class="field has-addons">
                  <div class="control is-expanded has-icons-left">
                    <input type="text"
                      class="input"
                      placeholder="Job Completion"
                      :value="inputValue"
                      v-on="inputEvents">
                    <span class="icon is-small is-left">
                      <i class="mdi mdi-calendar mdi-18px" />
                    </span>
                  </div>
                  <div class="control">
                    <a class="button tooltip"
                      data-tooltip="Reset dates"
                      @click="resetActualJobStartAndEnd()">
                      <span class="icon">
                        <i class="mdi mdi-close mdi-24px" />
                      </span>
                    </a>
                  </div>
                </div>
              </template>
            </v-date-picker>
          </div>
        </div>
      </div>

      <div class="field is-horizontal">
        <div class="field-body columns">
          <div class="field column mr-0">
            <label class="label">Vehicle Picked Up</label>
            <input v-if="readOnlyView"
              class="input is-static"
              :value="$filters.formatDateLocale(innerValue.vehicleDepartedDate, $userInfo.locale)"
              readonly>
            <v-date-picker v-else
              :is-dark="$theme.isDark"
              v-model="innerVehicleDepartedDate"
              ref="datepickerVehicleDeparted"
              mode="dateTime"
              :minute-increment="5"
              :masks="formats"
              :attributes="attrs"
              :locale="$userInfo.locale"
              class="is-fullwidth"
              :model-config="innerVehicleDepartedDate ? {} : modelConfig"
              :min-date="innerValue.arrivedForRepairDate"
              :timezone="$filters.getIanaTimezone()">
              <template v-slot="{ inputValue, inputEvents }">
                <div class="field has-addons">
                  <div class="control is-expanded has-icons-left">
                    <input type="text"
                      class="input"
                      placeholder="Vehicle Departed Date"
                      :value="inputValue"
                      v-on="inputEvents">
                    <span class="icon is-small is-left">
                      <i class="mdi mdi-calendar mdi-18px" />
                    </span>
                  </div>
                  <div class="control">
                    <a class="button tooltip"
                      data-tooltip="Reset dates"
                      @click="clearVehicleDepartedDate()">
                      <span class="icon">
                        <i class="mdi mdi-close mdi-24px" />
                      </span>
                    </a>
                  </div>
                </div>
              </template>
            </v-date-picker>
          </div>
          <div class="field column mr-0">
            <label class="label">Vehicle Departed</label>
            <div class="control">
              <div class="pretty p-switch p-fill">
                <input type="checkbox"
                  @change="isDepartedOnChange"
                  :checked="!!isDeparted">
                <div class="state p-primary">
                  <label>{{ isDeparted ? 'Yes' : 'No' }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- <div class="mt-6" /> -->
      <!-- <div class="field">
        <label class="label">Vehicle Picked Up</label>
        <div class="control">
          <v-date-picker v-model="vehiclePickupDate"
            :masks="formats"
            :attributes="attrs"
            :locale="$userInfo.locale"
            @input="vehiclePickupDateInputEvent"
            :timezone="$filters.getIanaTimezone()">
            <template v-slot="{ inputValue, inputEvents }">
              <div class="field">
                <div class="control has-icons-left">
                  <input type="text"
                    class="input"
                    placeholder="Vehicle Picked Up"
                    :value="inputValue"
                    v-on="inputEvents"
                    readonly>
                  <span class="icon is-small is-left">
                    <i class="mdi mdi-calendar mdi-18px" />
                  </span>
                </div>
              </div>
            </template>
          </v-date-picker>
        </div>
      </div> -->

    </article>
    <late-reason-modal v-if="isRepairLateReasonActive"
      :active.sync="isRepairLateReasonActive"
      @saveContinue="handleRepairLateReasonClose()"
      @close="handleRepairLateReasonClose()"
      :show-icon="true"
      :cancel-disabled="false"
      v-model="innerValue">
      <p slot="text-title">Late Reason</p>
    </late-reason-modal>
  </div>
</template>

<script>
import _cloneDeep from 'lodash.clonedeep'
import StoreMixin from '@/views/quote/storeMixin'
import PanelBlurMixin from './PanelBlurMixin'
import { FocusInserted } from '@/components/directives'
import { JobStageTypes, EventHubTypes } from '@/enums'
import InnerQuoteService from '@/views/quote/QuoteService'
import DeepDiff from 'deep-diff'
import LateReasonModal from '../LateReasonModal.vue'
import { DateTime } from 'luxon'

export default {
  name: 'QuoteBooking',
  inject: ['$vv'],
  directives: {
    FocusInserted
  },
  components: {
    LateReasonModal
  },
  mixins: [StoreMixin, PanelBlurMixin],
  props: {
    value: null,
    quote: null,
    insurer: null,
    isSaving: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      innerValue: null, //QuoteCycleTime
      innerQuote: null,

      innerQuoteDate: null,
      innerInspectionBookingDate: null,
      innerJobStartDate: null,
      innerJobEndDate: null,
      oriJobStartDate: null,
      oriJobEndDate: null,
      innerSentForAuthorizationDate: null,
      innerAuthorityDate: null,
      innerRepairBookedDate: null,
      innerArrivedForRepairDate: null,
      innerVehicleDepartedDate: null,
      innerActualJobStart: null,
      innerActualJobEnd: null,

      formats: {
        title: 'MMMM YYYY',
        weekdays: 'W',
        navMonths: 'MMM',
        input: ['L', 'DD-MM-YYYY', 'DD/MM/YYYY'],
        data: ['L', 'DD-MM-YYYY', 'DD/MM/YYYY'],
        dayPopover: 'L'
      },
      attrs: [
        {
          key: 'today',
          highlight: {
            backgroundColor: '#ff8080'
          },
          popover: {
            label: 'Today'
          },
          dates: new Date()
        }
      ],
      jobStartDateTime: null,
      jobCompletionDateTime: null,
      modelConfig: {
        start: {
          timeAdjust: '08:00:00'
        },
        end: {
          timeAdjust: '08:00:00'
        }
      },

      isArrived: false,
      isDeparted: false,
      // inspectionStartDate: null

      prevInnerValue: null,
      prevInnerQuote: null,
      isRepairLateReasonActive: false,

      bookedInSortKey: null,
      selectedJobStage: null,
      sortKey: null
    }
  },
  computed: {
    $v() {
      return this.$vv || this.$v
    },
    ormEstimateStartEndValidationText() {
      if (!this.$v.entity.jobStart.required || !this.$v.entity.jobEnd.required) {
        return 'Required by ORM'
      } else if (!this.$v.entity.jobStart.validJobStart || !this.$v.entity.jobEnd.validJobEnd) {
        return 'Invalid date range'
      }
    },
    isRepairBooked() {
      return this.innerQuote.isRepairBooked
    },
    isRepx() {
      return this.innerQuote.repx != null
    },
    isRepairStartDateSynced() {
      return this.quoteRepx ? this.quoteRepx.isRepairStartDateSynced : false
    }
  },
  watch: {
    innerValue: {
      handler: function (newVal, oldVal) {
        var diff = DeepDiff.diff(this.prevInnerValue, newVal)
        if (diff) {
          this.prevInnerValue = _cloneDeep(newVal)
          this.$emit('input', newVal)
        }
      },
      deep: true
    },
    innerQuote: {
      handler: function (newVal, oldVal) {
        var diff = DeepDiff.diff(this.prevInnerQuote, newVal)
        if (diff) {
          this.prevInnerQuote = _cloneDeep(newVal)
          this.$emit('update:quote', newVal)
        }
      },
      deep: true
    },
    innerQuoteDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerQuote.quoteDate = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerQuote.quoteDate = null
      }
    },
    innerInspectionBookingDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.inspectionBookingDate = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerValue.inspectionBookingDate = null
      }
    },
    innerSentForAuthorizationDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.sentForAuthorizationDate = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerValue.sentForAuthorizationDate = null
      }
    },
    innerAuthorityDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerQuote.authorisedDate = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerQuote.authorisedDate = null
      }
    },
    innerRepairBookedDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.repairBookedDate = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerValue.repairBookedDate = null
      }
    },
    innerArrivedForRepairDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.arrivedForRepairDate = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerValue.arrivedForRepairDate = null
      }
      this.isArrived = !!this.innerArrivedForRepairDate
    },
    innerActualJobStart: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.actualJobStart = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerValue.actualJobStart = null
      }
    },
    innerActualJobEnd: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.actualJobEnd = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerValue.actualJobEnd = null
      }
    },
    innerJobStartDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerQuote.jobStart = newVal.toISOString().split('.')[0] + 'Z'
        if (this.innerQuote.jobEnd && this.sortKey < this.bookedInSortKey && this.insurer.autoBookedInForDueInOut) {
          this.selectedJobStage = JobStageTypes.BookedIn
          this.innerQuote.jobStage = JobStageTypes.BookedIn
        }
      } else {
        this.innerQuote.jobStart = null
      }
    },
    innerJobEndDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerQuote.jobEnd = newVal.toISOString().split('.')[0] + 'Z'
        //to prompt late reason
        this.isRepairLateReasonActive = this.compare1DateTo2(newVal, this.oriJobEndDate)
        if (this.innerQuote.jobStart && this.sortKey < this.bookedInSortKey && this.insurer.autoBookedInForDueInOut) {
          this.selectedJobStage = JobStageTypes.BookedIn
          this.innerQuote.jobStage = JobStageTypes.BookedIn
        }
      } else {
        this.innerQuote.jobEnd = null
      }
    },
    innerVehicleDepartedDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.vehicleDepartedDate = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerValue.vehicleDepartedDate = null
      }
      this.isDeparted = !!this.innerVehicleDepartedDate
    },
    'innerQuote.isRepairBooked': function (newVal, oldVal) {
      if (newVal === false) {
        this.clearRepairBookedDate()
      }
    }
  },
  async created() {
    this.innerValue = _cloneDeep(this.value)
    this.innerQuote = _cloneDeep(this.quote)

    this.selectedJobStage = this.innerQuote.jobStage
    this.sortKey = this.getJobStageSortKey(this.innerQuote.jobStage)
    this.bookedInSortKey = this.getJobStageSortKey(JobStageTypes.BookedIn)

    this.innerInspectionBookingDate = (this.innerValue.inspectionBookingDate) ? new Date(`${this.innerValue.inspectionBookingDate}`) : null
    this.innerSentForAuthorizationDate = (this.innerValue.sentForAuthorizationDate) ? new Date(`${this.innerValue.sentForAuthorizationDate}`) : null
    this.innerRepairBookedDate = (this.innerValue.repairBookedDate) ? new Date(`${this.innerValue.repairBookedDate}`) : null
    this.innerArrivedForRepairDate = (this.innerValue.arrivedForRepairDate) ? new Date(`${this.innerValue.arrivedForRepairDate}`) : null
    this.innerVehicleDepartedDate = (this.innerValue.vehicleDepartedDate) ? new Date(`${this.innerValue.vehicleDepartedDate}`) : null
    this.innerActualJobStart = (this.innerValue.actualJobStart) ? new Date(`${this.innerValue.actualJobStart}`) : null
    this.innerActualJobEnd = (this.innerValue.actualJobEnd) ? new Date(`${this.innerValue.actualJobEnd}`) : null

    this.innerQuoteDate = this.innerToJsDate(this.innerQuote.quoteDate)
    this.innerAuthorityDate = this.innerToJsDate(this.innerQuote.authorisedDate)
    if (this.innerQuote.jobStart) {
      this.innerJobStartDate = this.innerToJsDate(this.innerQuote.jobStart)
      this.oriJobStartDate = this.innerJobStartDate
    }
    if (this.innerQuote.jobEnd) {
      this.innerJobEndDate = this.innerToJsDate(this.innerQuote.jobEnd)
      this.oriJobEndDate = this.innerJobEndDate
    }

    await this.loadQuoteRepxIfExist()
  },
  mounted() {
    this.$eventHub.$on(`${EventHubTypes.EntitySaved}-${this.$parent.$options.name}`, this.updateInnerValue)
  },
  beforeDestroy() {
    this.$eventHub.$off(`${EventHubTypes.EntitySaved}-${this.$parent.$options.name}`)
  },
  methods: {
    innerToJsDate(date) {
      if (this.$filters.isIsoDate(date)) {
        return this.$filters.fromISOToJsDate(date, this.$userInfo.locale)
      } else if (!isNaN(Date.parse(date))) {
        return new Date(date)
      } else {
        return null
      }
    },
    async loadQuoteRepxIfExist() {
      const quoteRepx = await InnerQuoteService.getQuoteRepx(this.innerQuote.quoteId)
      if (quoteRepx) {
        this.quoteRepx = quoteRepx
      }
    },
    isISODate(input) {
      const dt = DateTime.fromISO(input, { setZone: true })
      return dt.isValid
    },
    compare1DateTo2(dateStr1, dateStr2) {
      if (dateStr1 && dateStr2) {
        const date1 = new Date(dateStr1)
        const date2 = new Date(dateStr2)
        if (date1 > date2) {
          return true
        }
      }
      return false
    },
    getJobStageSortKey(stageId) {
      const index = this.jobStages.findIndex((i) => i.stage === stageId)
      const sortKey = this.jobStages[index].sortKey
      return sortKey
    },
    resetActualJobStartAndEnd() {
      if (this.innerValue.actualJobStart !== null) {
        this.innerValue.actualJobStart = null
      }
      if (this.innerValue.actualJobEnd !== null) {
        this.innerValue.actualJobEnd = null
      }
      this.innerActualJobStart = null
      this.innerActualJobEnd = null
    },
    resetJobStartEnd() {
      if (!this.isRepx || !this.isRepairStartDateSynced) {
        if (this.innerJobStartDate !== null) {
          this.innerJobStartDate = null
        }
      }
      if (this.innerJobEndDate !== null) {
        this.innerJobEndDate = null
      }
      if (this.selectedJobStage == JobStageTypes.BookedIn && this.insurer.autoBookedInForDueInOut) {
        this.selectedJobStage = JobStageTypes.FollowUpRequired
        this.innerQuote.jobStage = JobStageTypes.FollowUpRequired
      }
      // Reset validation objects
      this.$v.entity.jobStart.$reset()
      this.$v.entity.jobEnd.$reset()
    },
    isArrivedOnChange(event) {
      this.isArrived = !!event.target.checked
      if (!this.isArrived) {
        this.clearArrivedForRepairDate()
      } else {
        this.innerArrivedForRepairDate = new Date()
      }
    },
    isDepartedOnChange(event) {
      this.isDeparted = !!event.target.checked
      if (!this.isDeparted) {
        this.clearVehicleDepartedDate()
      } else {
        this.innerVehicleDepartedDate = new Date()
      }
    },
    clearArrivedForRepairDate() {
      if (this.innerArrivedForRepairDate !== null) {
        this.innerArrivedForRepairDate = null
        this.isArrived = false
      }
    },
    clearVehicleDepartedDate() {
      if (this.innerVehicleDepartedDate !== null) {
        this.innerVehicleDepartedDate = null
        this.isDeparted = false
      }
    },
    clearRepairBookedDate() {
      if (this.innerRepairBookedDate !== null) {
        this.innerRepairBookedDate = null
      }
    },
    handleRepairLateReasonClose() {
      this.isRepairLateReasonActive = false
    },
    async updateInnerValue() {
      console.log(`${this.$options.name} - ${this.value.modifiedDate}`)
      console.log(`${this.$options.name} - ${this.quote.modifiedDate}`)
      await this.$nextTick()
      this.innerValue = _cloneDeep(this.value)
      this.innerQuote = _cloneDeep(this.quote)
    }
  },
  beforeRouteEnter(to, from, next) {
    next()
  },
  beforeRouteLeave(to, from, next) {
    next()
  }
}
</script>

<style lang="scss" scoped>
@use '@/assets/style/override-colours' as oc;

.is-divider {
  margin: 0 0 1.2rem 0;
}
div.tile.is-parent {
  min-height: calc(100vh - 8rem);
}
.main-container > .field {
  margin-bottom: 1.5rem;
}
.is-disabled {
  pointer-events: none;
  opacity: 0.6;
}
</style>
